// Avatar tone hash — deterministic so a dog (or person) keeps its colour across
// renders and screens. Carried verbatim from _prototype/components.jsx.
const AVATAR_TONES = ['clay', 'sage', 'mist', 'mauve'] as const;

export function hashAvatarTone(id: string | undefined): string {
  let h = 0;
  const s = String(id ?? '');
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_TONES[h % AVATAR_TONES.length] ?? 'clay';
}

// People are circles, dogs are squircles carrying a paw badge — so a person and
// a pet are never confused at a glance (the same component renders staff,
// owners, account users, and dogs). The signal is shape + badge.
//
// THE FLOOR IS 18px, NOT 22. This comment claimed 22 "every avatar now sits
// at" while OwnerForm's own summary chips had been calling it at 18 all along,
// and the household modal's provenance marks now do too (Freddi, 2026-09-15).
// 22 is the floor where the DOG signal still reads — a squircle plus a paw at
// 18 is mush. A person's circle has no badge to lose, so it holds lower, and
// that is the only reason the smaller call sites are legitimate. Do not read
// this as licence to shrink a dog avatar.
export type AvatarKind = 'person' | 'dog';

export interface AvatarProps {
  name: string;
  id?: string;
  size?: number;
  kind?: AvatarKind;
  // Opt-in: when set, the avatar becomes a real button — e.g. open the edit-user
  // modal from anywhere a USER's avatar appears. Omitted everywhere else, so a
  // dog / owner / rota-staff avatar stays a plain, non-interactive span and is
  // never accidentally clickable.
  onClick?: () => void;
  // Tooltip + accessible label for the button form (falls back to `name`).
  title?: string;
  // WHAT TO DRAW IN PLACE OF THE INITIALS, for the one case that is not a
  // creature: a pile cut short, where the last box counts the dogs it is
  // standing in for. It keeps the squircle, the tone and the paw — it is still
  // a member of the pile, and Sir wants it to look like one — and only the two
  // letters are replaced by a number (2026-08-27).
  initials?: string;
}

// Stylised paw — a central pad and four toe-beans. Decorative; the name is the
// accessible identity, so the badge is aria-hidden.
function PawBadge() {
  return (
    <span className="avatar-paw" aria-hidden="true">
      <svg viewBox="0 0 100 100">
        <ellipse cx="26" cy="40" rx="8" ry="10.5" />
        <ellipse cx="42" cy="29" rx="8" ry="11.5" />
        <ellipse cx="58" cy="29" rx="8" ry="11.5" />
        <ellipse cx="74" cy="40" rx="8" ry="10.5" />
        <path d="M50 50c14 0 24 9.5 24 21 0 9-7.5 14-16 14-4.6 0-7.4-2-8-2s-3.4 2-8 2c-8.5 0-16-5-16-14 0-11.5 10-21 24-21z" />
      </svg>
    </span>
  );
}

export function Avatar({ name, id, size = 28, kind = 'person', onClick, title, initials }: AvatarProps) {
  const auto = (name || '?')
    .split(' ')
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  const tone = hashAvatarTone(id ?? name);
  const label = initials ?? auto;
  const face = (
    <span
      className={'avatar avatar-' + kind + ' tone-' + tone}
      style={{ width: size, height: size, fontSize: size * 0.4 }}
    >
      {label}
      {kind === 'dog' && <PawBadge />}
    </span>
  );
  if (!onClick) return face;
  return (
    <button type="button" className="avatar-btn" onClick={onClick} title={title ?? name} aria-label={title ?? name}>
      {face}
    </button>
  );
}
